import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';

type Resp = {
  trip: Record<string, unknown> & { id: string; name: string; status: string; createdAt: string };
  members: Array<{ userId: string; role: string; joinedAt: string; email: string | null; displayName: string | null }>;
  itineraryItems: Array<{
    id: string;
    dayNumber: number;
    orderIndex: number;
    type: string;
    title: string;
    description: string | null;
    location: string | null;
    startTime: string | null;
    endTime: string | null;
  }>;
};

export function TripDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const { data, isLoading } = useQuery<Resp>({
    queryKey: ['/admin/api/trips', id],
    queryFn: () => api.get<Resp>(`/admin/api/trips/${id}`),
    enabled: !!id,
  });

  const del = useMutation({
    mutationFn: () => api.delete(`/admin/api/trips/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['/admin/api/trips'] });
      nav('/trips');
    },
  });

  if (isLoading || !data) {
    return (
      <div>
        <PageHeader title="Trip" />
        <div className="ad-card p-6 text-text3">Loading…</div>
      </div>
    );
  }

  const days = Array.from(
    new Set(data.itineraryItems.map((i) => i.dayNumber)),
  ).sort((a, b) => a - b);

  return (
    <div>
      <PageHeader
        title={data.trip.name}
        subtitle={`Status: ${data.trip.status}`}
        actions={
          <Link to="/trips" className="ad-btn-secondary">
            <ArrowLeft size={14} /> Back
          </Link>
        }
      />

      <div className="ad-card p-6 mb-4">
        <h3 className="font-semibold mb-3">Members</h3>
        <table className="ad-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.userId}>
                <td>
                  <div>{m.displayName}</div>
                  <div className="text-text3 text-xs">{m.email}</div>
                </td>
                <td>{m.role}</td>
                <td className="text-text2 text-xs">{new Date(m.joinedAt).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ad-card p-6 mb-4">
        <h3 className="font-semibold mb-3">Itinerary ({data.itineraryItems.length})</h3>
        {days.length === 0 ? (
          <div className="text-text3 text-sm">No items.</div>
        ) : (
          days.map((d) => {
            const items = data.itineraryItems.filter((i) => i.dayNumber === d);
            return (
              <div key={d} className="mb-4 last:mb-0">
                <div className="text-xs uppercase tracking-wider text-text3 mb-2">Day {d}</div>
                <div className="space-y-1">
                  {items.map((i) => (
                    <div
                      key={i.id}
                      className="flex items-center gap-3 text-sm border border-border rounded-lg p-2.5"
                    >
                      <span className="ad-badge ad-badge-neutral text-[10px]">{i.type}</span>
                      <div className="flex-1">
                        <div>{i.title}</div>
                        {i.location && (
                          <div className="text-text3 text-xs">📍 {i.location}</div>
                        )}
                      </div>
                      {i.startTime && (
                        <div className="text-text2 text-xs">
                          {i.startTime}
                          {i.endTime && `–${i.endTime}`}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="ad-card p-6 border-error/30">
        <h3 className="font-semibold mb-3 text-error">Danger zone</h3>
        {!confirming ? (
          <button onClick={() => setConfirming(true)} className="ad-btn-danger">
            <Trash2 size={14} /> Delete trip
          </button>
        ) : (
          <div className="flex items-center gap-3">
            <button onClick={() => setConfirming(false)} className="ad-btn-secondary ad-btn-sm">
              Cancel
            </button>
            <button onClick={() => del.mutate()} disabled={del.isPending} className="ad-btn-danger ad-btn-sm">
              {del.isPending ? 'Deleting…' : 'Yes, delete'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
